import {
  Field,
  FieldControllerType,
  FormBuilder,
  FormBuilderContainer,
  GenericObj,
} from '@gemini/lux-ds-pms';
import useAnalytics, { AnalyticsActionType } from '@hooks/useAnalytics';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { usePatientStore } from '@store/patientStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import { Box, LuxTypography, useTheme } from '@gemini/fe-ds-core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';

interface NewPatientSectionProps {
  setEnableButton: (val: boolean) => void;
  submitAttemptCount?: number;
}

export const NewPatientSection = ({
  setEnableButton,
  submitAttemptCount = 0,
}: NewPatientSectionProps) => {
  const setIsNewPatientStarted = useOnlineBookingStore(
    state => state.setIsNewPatientStarted,
  );
  const { t } = useTranslation();
  const theme = useTheme();
  const { trackEventAnalytics } = useAnalytics();
  const setCurrentPatient = useOnlineBookingStore(
    state => state.setCurrentPatient,
  );
  const setShowAddPatientFrame = useOnlineBookingStore(
    state => state.setShowAddPatientFrame,
  );
  const updatePatient = usePatientStore(state => state.updatePatient);
  const patients = usePatientStore(state => state.patients);

  const setPatientOrder = usePatientStore(state => state.setPatientOrder);
  const methods = useRef<UseFormReturn<GenericObj> | null>(null);
  const [formIsReady, setFormIsReady] = useState(false);
  const showRequiredError = submitAttemptCount > 0;

  const newPatientField: Field = {
    labelKey: t('newPatientPage.description').replace(
      '{{maxPatientsPlaceholder}}',
      patients.filter(p => p).length > 1
        ? t('newPatientPage.one')
        : t('newPatientPage.two'),
    ),
    id: 'newPatientRadio',
    size: {
      xs: 12,
      md: 12,
      lg: 12,
    },
    type: FieldControllerType.RADIO,
    visibility: true,
    validations: [
      {
        type: 'required',
        value: true,
        errorMsgKey: 'fieldsLabel.errors.isRequired',
      },
    ],
    options: [
      { label: 'Yes', key: 'yes' },
      { label: 'No', key: 'no' },
    ],
    otherOptions: {
      radio: {
        wrapLabelText: true,
        variant: 'textMdRegular',
      },
    },
  };

  const handleNewPatient = (
    formData: { newPatientRadio: 'yes' | 'no' } | GenericObj | undefined,
  ) => {
    console.log('#unmount.newPatientSection');
    const isContinuingToContactDetails =
      useOnlineBookingStore.getState().currentFormStep ===
      StepsStep.CONTACT_DETAILS;
    const onlyTruePatient = patients.filter(p => p);
    if (
      formData &&
      formData.newPatientRadio === 'yes' &&
      isContinuingToContactDetails &&
      onlyTruePatient.length < 3
    ) {
      const emptyPatientIndex = patients.findIndex(p => !p);
      const newPatientIndex =
        emptyPatientIndex === -1 ? patients.length : emptyPatientIndex;
      const maxOrderPatient =
        patients.reduce((acc, patient) => {
          if (patient?.order) {
            return Math.max(acc, patient.order);
          }
          return acc;
        }, 0) + 1;

      updatePatient(newPatientIndex, FormIds.AppointmentType, {});
      setIsNewPatientStarted(true); // redirect to ApptType page
      setShowAddPatientFrame(false);
      setPatientOrder(newPatientIndex, maxOrderPatient);
      setCurrentPatient(newPatientIndex);
      trackEventAnalytics({
        action: AnalyticsActionType.ADD_PATIENT,
      });
    }
  };

  useEffect(() => {
    setEnableButton(false);
  }, [setEnableButton]);

  useEffect(() => {
    if (submitAttemptCount === 0 || !formIsReady || !methods.current) {
      return;
    }

    void methods.current.trigger('newPatientRadio');
  }, [formIsReady, submitAttemptCount]);

  return (
    <FormBuilderContainer
      key={'newPatientForm'}
      optionForm={{
        defaultValues: {},
        mode: 'onChange',
      }}
      setEnableButton={e => {
        setEnableButton(e);
      }}
      onUnmount={(formData: GenericObj | undefined) =>
        handleNewPatient(formData)
      }
      myRef={methods}
      setFormIsReady={setFormIsReady}
    >
      <FormBuilder form={[newPatientField]} defaultValues={{}}></FormBuilder>
      {showRequiredError && (
        <Box padding="0 1rem 1rem 1rem">
          <LuxTypography
            variant="textXs"
            style={{
              color: theme.palette.LUX.obTokens?.textColorFunctionalError,
              marginTop: '0.25rem',
            }}
          >
            {t('fieldsLabel.errors.isRequired')}
          </LuxTypography>
        </Box>
      )}
    </FormBuilderContainer>
  );
};
